import React, { useState, useEffect, createContext, useContext, ReactNode } from 'react';

import { Box, Button, Collapsible, Flex, Image, Input, Menu, Stack, Text } from '@chakra-ui/react';

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';

import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';

import { CSS } from '@dnd-kit/utilities';

import {
  CaretLeft,
  CaretDown,
  CaretUp,
  Check,
  DotsSix,
  DotsThreeVertical,
  PencilSimpleLine,
  Plus,
  Trash
} from '@phosphor-icons/react';

import { useNavigate, NavigateFunction } from 'react-router-dom';

import { fetchAPI } from '@/api/fetch';
import {
  apiGetQuestionBanks,
  GetQuestionBanksResponse,
  apiDeleteQuestion,
  apiUpdateQuestionOrder,
  apiUpdateCategories
} from '@/api/services';

import DeleteDialog from '@/components/dialogs/DeleteDialog';
import emptyJobImage from '@/assets/image/empty-job-cover.svg';
import QuestionBankEditor from './QuestionBankEditor';
import { LoadingSkeleton } from '@/components/layout/LoadingSkeleton';

interface CollapsibleContextType {
  collapsedCategories: Set<string>;
  toggleCategory: (category: string) => void;
  isCollapsed: (category: string) => boolean;
}

const CollapsibleContext = createContext<CollapsibleContextType | undefined>(undefined);

const useCollapsible = () => {
  const context = useContext(CollapsibleContext);
  if (!context) {
    throw new Error('useCollapsible must be used within a CollapsibleProvider');
  }
  return context;
};

const CollapsibleProvider = ({ children }: { children: ReactNode }) => {
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());

  const toggleCategory = (category: string) => {
    setCollapsedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  const isCollapsed = (category: string) => collapsedCategories.has(category);

  return (
    <CollapsibleContext.Provider value={{ collapsedCategories, toggleCategory, isCollapsed }}>
      {children}
    </CollapsibleContext.Provider>
  );
};

interface EmptyStateProps {
  onAddQuestion: () => void;
}

const EmptyState: React.FC<EmptyStateProps> = () => (
  <Stack w="100%" h="100%" align="center" role="region" aria-label="No interviews">
    <Image src={emptyJobImage} h="200px" w="200px" mt="64px" alt="No interviews illustration" />
    <Text fontSize="18px" color="neutrals.800" fontWeight="bold">
      No Question Banks Found
    </Text>
    <Text fontSize="16px" color="neutrals.500" mb="16px">
      Create your first question bank to get started
    </Text>
  </Stack>
);

interface CategoryQuestionBanks {
  category: string;
  questionBanks: QuestionBank[];
}

interface SortableQuestionBankProps {
  allowSort: boolean;
  questionBank: QuestionBank;
  selectedQuestion: QuestionBank | null;
  onSelect: (questionBank: QuestionBank) => void;
  onDelete: (questionBank: QuestionBank) => void;
}

function SortableQuestionBank({
  allowSort,
  questionBank,
  selectedQuestion,
  onSelect,
  onDelete
}: SortableQuestionBankProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: questionBank.question_id
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 1 : 0.5
  };

  return (
    <Flex align="center" gap="8px" mb="12px">
      {allowSort && (
        <Box
          {...attributes}
          {...listeners}
          cursor="grab"
          _active={{ cursor: 'grabbing' }}
          p="4px"
          borderRadius="4px"
          _hover={{ bg: 'neutrals.100' }}
          onClick={(e) => e.stopPropagation()}
        >
          <DotsSix size={20} color="#9CA3AF" />
        </Box>
      )}
      <Box
        ref={setNodeRef}
        style={style}
        key={questionBank.question_id}
        border="1px solid"
        borderColor="neutrals.200"
        borderRadius="12px"
        p="16px"
        cursor="pointer"
        bg={
          selectedQuestion?.question_id === questionBank.question_id ? 'neutrals.100' : 'neutrals.0'
        }
        _hover={{ bg: 'neutrals.50' }}
        onClick={() => onSelect(questionBank)}
        flex="1"
      >
        <Flex justify="space-between" align="center">
          <Stack>
            <Text fontSize="16px" fontWeight="semibold" color="neutrals.800">
              {questionBank.job_title}
            </Text>
            <Text fontSize="12px" color="neutrals.500">
              {questionBank.questions.length} question
              {questionBank.questions.length !== 1 ? 's' : ''}
            </Text>
          </Stack>
          <Menu.Root positioning={{ placement: 'bottom-start' }}>
            <Menu.Trigger asChild>
              <Button
                variant="ghost"
                color="neutrals.700"
                borderRadius="full"
                p="0"
                _hover={{ bg: 'neutrals.100' }}
                onClick={(e) => e.stopPropagation()}
              >
                <DotsThreeVertical size={24} weight="bold" />
              </Button>
            </Menu.Trigger>
            <Menu.Positioner zIndex={1000}>
              <Menu.Content borderRadius="8px">
                <Menu.Item
                  value="delete"
                  fontSize="16px"
                  cursor="pointer"
                  color="error.500"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(questionBank);
                  }}
                >
                  <Flex align="center" gap="8px">
                    <Trash size={16} />
                    Delete
                  </Flex>
                </Menu.Item>
              </Menu.Content>
            </Menu.Positioner>
          </Menu.Root>
        </Flex>
      </Box>
    </Flex>
  );
}

interface EditCategoryProps {
  category: string | null;
  updatedValue: string | null;
  isUpdating: boolean;
  error: string | null;
}

function QuestionBankIndexContent() {
  const navigate = useNavigate();
  const { isCollapsed, toggleCategory } = useCollapsible();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [questionBanks, setQuestionBanks] = useState<CategoryQuestionBanks[]>([]);
  const [selectedQuestion, setSelectedQuestion] = useState<QuestionBank | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<QuestionBank | null>(null);
  const [editingCategory, setEditingCategory] = useState<EditCategoryProps>({
    category: null,
    updatedValue: null,
    isUpdating: false,
    error: null
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const fetchQuestionBanks = async (loading: boolean) => {
    if (loading) {
      setIsLoading(true);
    }

    setError(null);

    try {
      const response = await fetchAPI<GetQuestionBanksResponse>({
        request: () => apiGetQuestionBanks()
      });

      if (response.success) {
        const banks = response.data.question_banks;
        const categoriesMap: Record<string, QuestionBank[]> = {};

        banks.forEach((bank) => {
          if (!categoriesMap[bank.category]) {
            categoriesMap[bank.category] = [];
          }
          categoriesMap[bank.category].push(bank);
        });

        const categories = Object.keys(categoriesMap).map((category) => ({
          category,
          questionBanks: categoriesMap[category]
        }));

        setQuestionBanks(categories);
      } else {
        setError('Failed to fetch question banks');
      }
    } catch (error) {
      console.error('Error fetching question banks:', error);
      setError('An error occurred while fetching question banks');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchQuestionBanks(true);
  }, []);

  const handleSubmit = async (questionBank: QuestionBank) => {
    fetchQuestionBanks(false);
    setSelectedQuestion(questionBank);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const activeId = active.id as string;
    const overId = over.id as string;

    // Find which category contains the dragged item
    let sourceCategoryIndex = -1;
    let activeIndex = -1;
    let overIndex = -1;
    let targetCategoryIndex = -1;

    questionBanks.forEach((category, categoryIndex) => {
      const activeIdx = category.questionBanks.findIndex((bank) => bank.question_id === activeId);
      const overIdx = category.questionBanks.findIndex((bank) => bank.question_id === overId);

      if (activeIdx !== -1) {
        sourceCategoryIndex = categoryIndex;
        activeIndex = activeIdx;
      }
      if (overIdx !== -1) {
        targetCategoryIndex = categoryIndex;
        overIndex = overIdx;
      }
    });

    // Only allow reordering within the same category
    if (sourceCategoryIndex !== targetCategoryIndex || sourceCategoryIndex === -1) {
      return;
    }

    const newQuestionBanks = [...questionBanks];
    const categoryBanks = [...newQuestionBanks[sourceCategoryIndex].questionBanks];
    const reorderedBanks = arrayMove(categoryBanks, activeIndex, overIndex);

    newQuestionBanks[sourceCategoryIndex] = {
      ...newQuestionBanks[sourceCategoryIndex],
      questionBanks: reorderedBanks
    };

    setQuestionBanks(newQuestionBanks);

    // Update the order on the server
    try {
      const draggedBank = reorderedBanks[overIndex];
      const response = await fetchAPI({
        request: () =>
          apiUpdateQuestionOrder({
            questionId: draggedBank.question_id,
            payload: { order: overIndex }
          })
      });
      if (!response.success) {
        console.error('Failed to update question order on server:', response.message);
        // Revert the order change on failure
        fetchQuestionBanks(false);
      }
    } catch (error) {
      console.error('Error updating question order:', error);
      // Revert the order change on error
      fetchQuestionBanks(false);
    }
  };

  const handleUpdateCategory = async (category: string, newCategory: string) => {
    if (!newCategory.trim()) {
      setEditingCategory({
        ...editingCategory,
        error: 'Category name cannot be empty'
      });
      return;
    }
    if (category === newCategory) {
      setEditingCategory({
        category: null,
        updatedValue: null,
        isUpdating: false,
        error: null
      });
    }

    try {
      const response = await fetchAPI({
        request: () =>
          apiUpdateCategories({
            payload: { old_category: category, new_category: newCategory }
          })
      });

      if (response.success) {
        // Update local state
        const updatedCategories = questionBanks.map((cat) => {
          if (cat.category === category) {
            return { ...cat, category: newCategory };
          }
          return cat;
        });
        setQuestionBanks(updatedCategories);

        // Clear editing state
        setEditingCategory({
          category: null,
          updatedValue: null,
          isUpdating: false,
          error: null
        });
      } else {
        console.error('Failed to update category:', response.message);
        setEditingCategory({
          ...editingCategory,
          error: response.message || 'Failed to update category. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error updating category:', error);
      setEditingCategory({
        ...editingCategory,
        error: 'Failed to update category. Please try again.'
      });
      return;
    }
  };

  const handleDelete = (questionBank: QuestionBank) => {
    setDeleteConfirmation(questionBank);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmation) return;

    const response = await fetchAPI({
      request: () => apiDeleteQuestion({ questionId: deleteConfirmation.question_id })
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to delete question bank');
    }

    // Remove from local state
    const newQuestionBanks = questionBanks
      .map((category) => ({
        ...category,
        questionBanks: category.questionBanks.filter(
          (bank) => bank.question_id !== deleteConfirmation.question_id
        )
      }))
      .filter((category) => category.questionBanks.length > 0);

    setQuestionBanks(newQuestionBanks);

    // Clear selection if deleted question was selected
    if (selectedQuestion?.question_id === deleteConfirmation.question_id) {
      setSelectedQuestion(null);
    }
  };

  if (error) {
    return (
      <Flex w="100%" h="100%" direction="column" align="center" justify="center" role="alert">
        <Text color="error.500" fontSize="16px">
          {error}
        </Text>
        <Button
          border="1px solid"
          borderColor="neutrals.300"
          borderRadius="8px"
          p="12px 24px"
          color="neutrals.700"
          bg="neutrals.50"
          _hover={{ bg: 'neutrals.100' }}
          _active={{ bg: 'neutrals.200' }}
          mt="16px"
          fontSize="16px"
          onClick={() => fetchQuestionBanks(true)}
        >
          Retry
        </Button>
      </Flex>
    );
  }

  return (
    <Box w="100%" h="100vh" p="40px 48px" bg="background.primary" overflow="hidden">
      <Flex mb="32px">
        <BreadcrumbNavigation onNavigate={navigate} />
      </Flex>

      <Flex w="100%" h="calc(100vh - 40px - 48px - 32px )" gap="24px">
        <Box
          flex="1"
          borderRadius="12px"
          bg="background.secondary"
          p="32px"
          h="100%"
          overflow="hidden"
        >
          <Flex justify="space-between" align="center" mb="24px">
            <Text fontSize="18px" fontWeight="bold" color="neutrals.800">
              Question Bank
            </Text>
            <Button
              border="1px solid"
              borderRadius="8px"
              borderColor="primary.900"
              color="primary.900"
              bg="neutrals.0"
              fontSize="14px"
              _hover={{ bg: 'neutrals.50' }}
              disabled={isLoading}
              p="0 8px"
              onClick={() => setSelectedQuestion(null)}
            >
              <Plus size={16} />
              Add
            </Button>
          </Flex>

          {isLoading ? (
            <LoadingSkeleton count={3} />
          ) : (
            <Box h="calc(100% - 72px)" overflowY="auto">
              {questionBanks.length === 0 ? (
                <EmptyState onAddQuestion={() => navigate('/interview/question-bank')} />
              ) : (
                questionBanks.map((category, index) => (
                  <Box key={index} mb="24px">
                    <Flex
                      justify="space-between"
                      alignContent="center"
                      alignItems="center"
                      cursor={
                        editingCategory.category === category.category ? 'default' : 'pointer'
                      }
                      mb="12px"
                      onClick={() =>
                        editingCategory.category !== category.category &&
                        toggleCategory(category.category)
                      }
                    >
                      <Flex gap="12px" alignContent="center" alignItems="center">
                        {editingCategory.category === category.category ? (
                          <Stack gap="2px">
                            <Flex>
                              <Box p="2px">
                                <Input
                                  fontSize="16px"
                                  fontWeight="bold"
                                  color="neutrals.700"
                                  border="1px solid"
                                  borderColor={editingCategory.error ? 'error.500' : 'neutrals.200'}
                                  borderRadius="8px"
                                  p="8px 12px"
                                  defaultValue={category.category}
                                  onChange={(e) =>
                                    setEditingCategory({
                                      ...editingCategory,
                                      updatedValue: e.target.value,
                                      error: null
                                    })
                                  }
                                />
                              </Box>
                              <Button
                                p="0"
                                variant="ghost"
                                color="primary.900"
                                onClick={() =>
                                  handleUpdateCategory(
                                    category.category,
                                    editingCategory.updatedValue || ''
                                  )
                                }
                              >
                                <Check size={16} />
                              </Button>
                            </Flex>
                            {editingCategory.error && (
                              <Text color="error.500" fontSize="12px">
                                {editingCategory.error}
                              </Text>
                            )}
                          </Stack>
                        ) : (
                          <>
                            <Text fontSize="16px" fontWeight="bold" color="neutrals.700">
                              {category.category}
                            </Text>
                            <Button
                              p="0"
                              variant="ghost"
                              color="primary.900"
                              onClick={(e) => {
                                e.stopPropagation();

                                if (isCollapsed(category.category)) {
                                  toggleCategory(category.category);
                                }

                                setEditingCategory({
                                  ...editingCategory,
                                  category: category.category,
                                  updatedValue: category.category
                                });
                              }}
                            >
                              <PencilSimpleLine size={16} />
                            </Button>
                          </>
                        )}
                      </Flex>
                      <Box color="neutrals.700">
                        {isCollapsed(category.category) ? (
                          <CaretDown size={20} />
                        ) : (
                          <CaretUp size={20} />
                        )}
                      </Box>
                    </Flex>
                    <Collapsible.Root open={!isCollapsed(category.category)}>
                      <Collapsible.Content>
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragEnd={handleDragEnd}
                        >
                          <SortableContext
                            items={category.questionBanks.map((bank) => bank.question_id)}
                            strategy={verticalListSortingStrategy}
                          >
                            {category.questionBanks.map((bank) => (
                              <SortableQuestionBank
                                key={bank.question_id}
                                allowSort={editingCategory.category === category.category}
                                questionBank={bank}
                                selectedQuestion={selectedQuestion}
                                onSelect={setSelectedQuestion}
                                onDelete={handleDelete}
                              />
                            ))}
                          </SortableContext>
                        </DndContext>
                      </Collapsible.Content>
                    </Collapsible.Root>
                  </Box>
                ))
              )}
            </Box>
          )}
        </Box>

        <Box
          flex="2"
          borderRadius="12px"
          bg="background.secondary"
          h="100%"
          overflow="hidden"
          p="32px"
        >
          <QuestionBankEditor
            isLoading={isLoading}
            questionBank={selectedQuestion}
            onCancel={() => setSelectedQuestion(null)}
            onSubmit={handleSubmit}
          />
        </Box>
      </Flex>

      {/* Delete Confirmation Dialog */}
      <DeleteDialog
        isOpen={!!deleteConfirmation}
        onClose={() => setDeleteConfirmation(null)}
        title="Delete Question Bank"
        itemName={deleteConfirmation?.job_title}
        itemType="question bank"
        onConfirm={confirmDelete}
      />
    </Box>
  );
}

export default function QuestionBankIndex() {
  return (
    <CollapsibleProvider>
      <QuestionBankIndexContent />
    </CollapsibleProvider>
  );
}

function BreadcrumbNavigation({ onNavigate }: { onNavigate: NavigateFunction }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="center" h="100%" gap="16px">
      <Box as="button" onClick={() => onNavigate(`/interview?tab=automated`)} cursor="pointer">
        <CaretLeft size={28} color="#545454" />
      </Box>
      <Box fontSize="16px" color="neutrals.700">
        <Text
          as="span"
          cursor="pointer"
          onClick={() => onNavigate(`/interview?tab=automated`)}
          _hover={{ textDecoration: 'underline' }}
        >
          Interview
        </Text>{' '}
        <Text as="span">/ </Text>
        <Text
          as="b"
          cursor="pointer"
          onClick={() => onNavigate(`/interview/question-bank`)}
          _hover={{ textDecoration: 'underline' }}
        >
          Question Management
        </Text>{' '}
      </Box>
    </Stack>
  );
}
